/**
 * Landing — today's rates for every loan program as a horizontal
 * (programs-down-the-side) bar chart sorted lowest first. Each bar is the
 * published wholesale rate; its dark segment is the typical Unbeatable Loans
 * customer rate (wholesale − UNBEATABLE_RATE_DISCOUNT), and the light
 * remainder is the difference. The month-over-month change in the published
 * rate is printed inside the dark segment when it fits.
 *
 * Reads the public active rate config; renders nothing if it can't load so
 * the landing page never advertises placeholder rates. Every rate shown
 * carries its APR, no less prominently (Reg Z §1026.24(c)); a program whose
 * APR can't be computed is left out.
 */

import { useMemo } from 'react';
import { Box, Container, Stack, Typography, useTheme } from '@mui/material';
import { format, parseISO } from 'date-fns';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Tooltip as RechartTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import {
  useGetLatestInterestRatesQuery,
  useGetPublishedRateHistoryQuery,
} from '@/apis/extended/globalApi';
import {
  AGENT_COMMISSION_PERCENT,
  AGENT_SAVINGS,
  EXAMPLE_PRICE,
  LandingAgentSavingsChart,
} from '@/components/Landing/LandingAgentSavingsChart';
import {
  ADVERTISED_LOAN_SHAPE,
  advertisedApr,
  REPRESENTATIVE_LOAN_AMOUNT,
} from '@/utils/advertisedApr';
import { advertisedFeesFor } from '@/utils/advertisedFees';
import { calculateClosingDisclosure } from '@/utils/calculator';
import { monthOverMonthChange } from '@/utils/rateChange';

/**
 * How far below the published wholesale rate a typical Unbeatable Loans
 * customer's rate is. Marketing claim — must stay substantiated by actual
 * closed-loan data; change it here and every figure and disclosure follows.
 */
const UNBEATABLE_RATE_DISCOUNT = 0.5;

/** Unbeatable rate segment: validated slot-1 blue on the white card. */
const UNBEATABLE_COLOR = '#2a78d6';
/** Wholesale remainder: lighter step of the same ramp (labels carry values). */
const WHOLESALE_COLOR = '#78aef0';
const GRID_COLOR = '#e1e0d9';
const BAR_THICKNESS = 22;
const ROW_HEIGHT = 42;
/** Screen-reader-only: keeps the table view in the accessibility tree. */
const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

const PROGRAM_GROUP: Record<string, string> = {
  conventional: 'Home purchase',
  fha: 'Home purchase',
  va: 'Home purchase',
  usda: 'Home purchase',
  dscr: 'Investor',
  commercial: 'Investor',
  fixAndFlip: 'Investor',
  bridge: 'Investor',
  heloc: 'Home equity',
  heloan: 'Home equity',
  secondMortgage: 'Home equity',
};

interface RateRow {
  key: string;
  label: string;
  group: string;
  /** Published wholesale rate and its APR. */
  rate: number;
  apr: number;
  /** Typical Unbeatable Loans customer rate and its APR. */
  ubRate: number;
  ubApr: number;
  /** Stacked segment lengths: ubRate + savings = rate. */
  savings: number;
  /** Change in the published rate vs. ~1 month ago, or null if unknown. */
  mom: number | null;
  /** Bar-tip label: Unbeatable rate and APR, same size and weight. */
  tipLabel: string;
  /** Inside-bar label: month-over-month change. */
  momLabel: string;
}

/** 6.5 → "6.50%", 6.475 → "6.475%". */
const formatRate = (rate: number) => {
  const fixed = rate.toFixed(3).replace(/0$/, '');
  return `${fixed}%`;
};

const usd = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

/** -0.196 → "▼ 0.20% vs last month"; 0 → "No change vs last month". */
const formatMom = (mom: number | null) => {
  if (mom == null) return '';
  if (Math.abs(mom) < 0.005) return 'No change vs last month';
  return `${mom > 0 ? '▲' : '▼'} ${Math.abs(mom).toFixed(2)}% vs last month`;
};

const RateTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: RateRow }[];
}) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const line = (color: string, name: string, rate: number, apr: number) => (
    <Stack direction="row" alignItems="center" gap={1}>
      <Box
        sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }}
      />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {name}
      </Typography>
      <Typography
        variant="body2"
        sx={{ ml: 2, fontVariantNumeric: 'tabular-nums' }}
      >
        {formatRate(rate)} · {formatRate(apr)} APR
      </Typography>
    </Stack>
  );
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        px: 1.5,
        py: 1,
        boxShadow: 3,
        minWidth: 280,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {row.group}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {row.label}
      </Typography>
      {line(UNBEATABLE_COLOR, 'Unbeatable rate', row.ubRate, row.ubApr)}
      {line(WHOLESALE_COLOR, 'Wholesale rate', row.rate, row.apr)}
      {row.mom != null && (
        <Typography variant="caption" color="text.secondary">
          {formatMom(row.mom)}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Month-over-month label inside the Unbeatable segment — only when it fits
 * with padding; otherwise the tooltip and table still carry it.
 */
const MomInsideLabel = (props: {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  value?: unknown;
}) => {
  const text = String(props.value ?? '');
  const x = Number(props.x);
  const y = Number(props.y);
  const width = Number(props.width);
  const height = Number(props.height);
  if (!text || !Number.isFinite(width)) return null;
  const needed = text.length * 6.2 + 16;
  if (width < needed) return null;
  return (
    <text
      x={x + 8}
      y={y + height / 2}
      dominantBaseline="central"
      fill="#ffffff"
      fontSize={11}
      fontWeight={600}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {text}
    </text>
  );
};

export const LandingRateChart = () => {
  const theme = useTheme();
  const { data, isError } = useGetLatestInterestRatesQuery();
  const { data: history } = useGetPublishedRateHistoryQuery(45);

  const rows = useMemo<RateRow[]>(() => {
    const programs = data?.data?.programs ?? {};
    return Object.entries(programs)
      .filter(([key, p]) => PROGRAM_GROUP[key] && p?.baseRate > 0)
      .flatMap(([key, p]) => {
        const shape = ADVERTISED_LOAN_SHAPE[key];
        const rate = p.baseRate;
        const ubRate = Math.max(0, rate - UNBEATABLE_RATE_DISCOUNT);
        // Lender fees come from the program's closing-cost calculator (with
        // admin overrides), the same figures every estimate uses; the
        // published assumptions supply the government fee percentages.
        const fees = advertisedFeesFor(key, rate, p);
        const assumptions = fees
          ? {
              ...p.aprAssumptions,
              underwritingFee: 0,
              processingFee: 0,
              applicationFee: 0,
              creditReportFee: 0,
              otherFees: 0,
              lenderFees: fees.lenderFees,
              prepaidInterestDays: fees.prepaidInterestDays,
              ...fees.government,
            }
          : p.aprAssumptions;
        const apr = advertisedApr(rate, shape, assumptions);
        const ubApr = advertisedApr(ubRate, shape, assumptions);
        if (apr == null || ubApr == null) return [];
        const mom = monthOverMonthChange(history?.data, key, rate);
        return [
          {
            key,
            label: p.label,
            group: PROGRAM_GROUP[key],
            rate,
            apr,
            ubRate,
            ubApr,
            savings: rate - ubRate,
            mom,
            tipLabel: `${formatRate(ubRate)} · ${formatRate(ubApr)} APR`,
            momLabel: formatMom(mom),
          },
        ];
      })
      .sort((a, b) => a.rate - b.rate);
  }, [data, history]);

  // Cash purchase, the base case: cash to close from the Cash calculator,
  // with and without the buyer's agent commission taken off at closing.
  const cash = useMemo(() => {
    const est = calculateClosingDisclosure({
      type: USER_LOAN_PROGRAM.CASH,
      listPrice: EXAMPLE_PRICE,
      offerPrice: EXAMPLE_PRICE,
      commission: AGENT_COMMISSION_PERCENT,
    });
    const deal = (est as { unbeatableDeal?: number }).unbeatableDeal;
    return {
      closingCosts: est.closingCosts,
      cashToClose: est.cashToClose,
      withSavings: deal ?? est.cashToClose - AGENT_SAVINGS,
    };
  }, []);

  const programLabels = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(data?.data?.programs ?? {}).map(([k, p]) => [
          k,
          p?.label,
        ])
      ),
    [data]
  );

  if (isError || rows.length === 0) return null;

  const lowest = rows[0];
  const highest = rows[rows.length - 1];
  const domainMax = Math.ceil(highest.rate + 1);
  const ticks = Array.from({ length: domainMax + 1 }, (_, i) => i).filter(
    (t) => t % 2 === 0 || t === domainMax
  );
  const publishedAt =
    data?.data?.metadata?.publishedAt ?? data?.data?.updatedAt ?? null;
  const hasMom = rows.some((r) => r.mom != null);

  const textMuted = theme.palette.text.secondary;

  return (
    <Box component="section" sx={{ py: { xs: 8, md: 10 } }}>
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={{ xs: 4, md: 8 }}
          alignItems={{ md: 'center' }}
        >
          <Box sx={{ flex: '0 0 auto', maxWidth: { md: 360 } }}>
            <Typography
              variant="overline"
              sx={{ color: textMuted, letterSpacing: 1.5 }}
            >
              Cash purchase — the base case
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 40, md: 44 },
                fontWeight: 600,
                lineHeight: 1.05,
                color: theme.palette.text.primary,
                mt: 1,
              }}
            >
              Save {usd(AGENT_SAVINGS)}
            </Typography>
            <Typography variant="body1" sx={{ color: textMuted, mt: 1.5 }}>
              on a {usd(EXAMPLE_PRICE)} home with no buyer&apos;s agent — the{' '}
              {AGENT_COMMISSION_PERCENT}% commission you keep. Offer{' '}
              {usd(EXAMPLE_PRICE - AGENT_SAVINGS)} instead, or cut your cash to
              close from {usd(cash.cashToClose)} to {usd(cash.withSavings)}.
              Every loan program saves the same — see the green bars.
            </Typography>

            <Typography
              variant="overline"
              sx={{
                color: textMuted,
                letterSpacing: 1.5,
                display: 'block',
                mt: 4,
              }}
            >
              Today&apos;s rates by loan program
            </Typography>
            <Stack direction="row" gap={3} sx={{ mt: 1 }} flexWrap="wrap">
              {[
                { value: lowest.ubRate, caption: 'Unbeatable rate' },
                { value: lowest.ubApr, caption: 'APR' },
              ].map(({ value, caption }) => (
                <Box key={caption}>
                  <Typography
                    sx={{
                      fontSize: { xs: 40, md: 44 },
                      fontWeight: 600,
                      lineHeight: 1.05,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatRate(value)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: textMuted }}>
                    {caption}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Typography variant="body1" sx={{ color: textMuted, mt: 2 }}>
              Typical rate for Unbeatable Loans customers on a {lowest.label}{' '}
              loan — {UNBEATABLE_RATE_DISCOUNT.toFixed(2)}% below today&apos;s
              wholesale rate of {formatRate(lowest.rate)} (
              {formatRate(lowest.apr)} APR).
            </Typography>
            {publishedAt && (
              <Typography
                variant="caption"
                sx={{ color: textMuted, display: 'block', mt: 2 }}
              >
                Published {format(parseISO(publishedAt), 'MMM d, yyyy')}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              position: 'relative',
              bgcolor: '#ffffff',
              border: '1px solid',
              borderColor: theme.palette.greyShades.border1,
              borderRadius: 3,
              p: { xs: 2, md: 3 },
            }}
          >
            {/* Legend: two series, so identity is never color alone. */}
            <Stack
              direction="row"
              gap={3}
              flexWrap="wrap"
              sx={{ mb: 2, pl: { sm: '140px' } }}
            >
              {[
                { color: UNBEATABLE_COLOR, name: 'Unbeatable rate (typical)' },
                { color: WHOLESALE_COLOR, name: 'Wholesale rate' },
              ].map(({ color, name }) => (
                <Stack key={name} direction="row" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '3px',
                      bgcolor: color,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: textMuted }}>
                    {name}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Box
              role="img"
              aria-label={`Unbeatable and wholesale rates by loan program. Lowest: ${
                lowest.label
              }, Unbeatable rate ${formatRate(lowest.ubRate)} (${formatRate(
                lowest.ubApr
              )} APR), wholesale ${formatRate(lowest.rate)} (${formatRate(
                lowest.apr
              )} APR). Highest: ${highest.label}, Unbeatable rate ${formatRate(
                highest.ubRate
              )} (${formatRate(highest.ubApr)} APR).`}
              sx={{ height: rows.length * ROW_HEIGHT + 40 }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={rows}
                  layout="vertical"
                  margin={{ top: 0, right: 150, bottom: 0, left: 0 }}
                  barSize={BAR_THICKNESS}
                >
                  <CartesianGrid
                    horizontal={false}
                    stroke={GRID_COLOR}
                    strokeWidth={1}
                  />
                  <XAxis
                    type="number"
                    domain={[0, domainMax]}
                    ticks={ticks}
                    tickFormatter={(v: number) => `${v}%`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: textMuted, fontSize: 12 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={140}
                    axisLine={{ stroke: GRID_COLOR }}
                    tickLine={false}
                    tick={{ fill: theme.palette.text.primary, fontSize: 13 }}
                  />
                  <RechartTooltip
                    content={<RateTooltip />}
                    cursor={{ fill: theme.palette.action.hover }}
                  />
                  {/* 2px surface seam between the two segments. */}
                  <Bar
                    dataKey="ubRate"
                    stackId="rate"
                    fill={UNBEATABLE_COLOR}
                    stroke="#ffffff"
                    strokeWidth={1}
                    animationDuration={900}
                  >
                    <LabelList dataKey="momLabel" content={MomInsideLabel} />
                  </Bar>
                  <Bar
                    dataKey="savings"
                    stackId="rate"
                    fill={WHOLESALE_COLOR}
                    stroke="#ffffff"
                    strokeWidth={1}
                    radius={[0, 4, 4, 0]}
                    animationDuration={900}
                  >
                    <LabelList
                      dataKey="tipLabel"
                      position="right"
                      style={{
                        fill: theme.palette.text.primary,
                        fontSize: 12,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={visuallyHidden}>
              <table>
                <caption>
                  Unbeatable and wholesale rates and APRs by loan program
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Loan program</th>
                    <th scope="col">Category</th>
                    <th scope="col">Unbeatable rate</th>
                    <th scope="col">Unbeatable APR</th>
                    <th scope="col">Wholesale rate</th>
                    <th scope="col">Wholesale APR</th>
                    <th scope="col">Change vs last month</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key}>
                      <th scope="row">{r.label}</th>
                      <td>{r.group}</td>
                      <td>{formatRate(r.ubRate)}</td>
                      <td>{formatRate(r.ubApr)}</td>
                      <td>{formatRate(r.rate)}</td>
                      <td>{formatRate(r.apr)}</td>
                      <td>{r.momLabel || 'Not available'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>

            <Typography
              variant="caption"
              sx={{ color: textMuted, display: 'block', mt: 2 }}
            >
              Rates for illustration only, not an offer to lend. Wholesale rate
              is today&apos;s published base rate. Unbeatable rate is the
              typical rate for Unbeatable Loans customers,{' '}
              {UNBEATABLE_RATE_DISCOUNT.toFixed(2)}% below the wholesale rate;
              your rate may be higher or lower.
              {hasMom &&
                ' Monthly change compares the published wholesale rate with the rate published about 30 days earlier.'}{' '}
              APR is calculated on a $
              {REPRESENTATIVE_LOAN_AMOUNT.toLocaleString()} loan using each
              program&apos;s lender fees, 15 days of prepaid interest, and
              government fees: a 30-year fixed term for purchase, investor, home
              equity loan, and second mortgage programs; a 12-month
              interest-only term with a final balloon payment for Fix and Flip
              and Bridge; HELOC APR is variable and excludes fees. FHA includes
              upfront and annual mortgage insurance; VA includes the funding
              fee; USDA includes the upfront and annual guarantee fees. Your
              rate and APR depend on credit score, loan-to-value, property type,
              and other factors.
            </Typography>

            <Box
              sx={{
                mt: 4,
                pt: 4,
                borderTop: '1px solid',
                borderColor: theme.palette.greyShades.border1,
              }}
            >
              <LandingAgentSavingsChart labels={programLabels} />
              <Typography
                variant="caption"
                sx={{ color: textMuted, display: 'block', mt: 2 }}
              >
                Example only. Typical down payments are each program&apos;s
                usual starting point: 5% conventional, 3.5% FHA, 0% VA and USDA,
                25% investor programs. Cash to close for the cash purchase is
                the price plus {usd(cash.closingCosts)} of estimated closing
                costs (title, recording, transfer taxes and prepaid insurance,
                using national-average taxes). Commission arrangements vary and
                are negotiable.
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};
